import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { userAdminAPI } from '../services/api';
import { formatKstDateTime } from '../utils/datetime';

const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(n);

const SOURCE_LABEL: Record<string, string> = {
  work_reward: '근무기록 수당', ad_reward: '광고 보상', gifticon_redeem: '기프티콘 교환',
  gifticon_refund: '발급 환불', admin_grant: '관리자 지급', admin_confiscate: '관리자 몰수', signup_bonus: '가입 보너스',
  offerwall_tnk: '오퍼월(TNK)', offerwall_adpopcorn: '오퍼월(AdPopcorn)',
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  // 목록에서 들어왔으면 보던 페이지·검색 조건으로 돌아감
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch ?? '';
  const queryClient = useQueryClient();

  const [pointDialog, setPointDialog] = useState(false);
  const [pointType, setPointType] = useState<'grant' | 'confiscate'>('grant');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');

  const { data: user } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: async () => (await userAdminAPI.getById(userId)).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });

  const pointMutation = useMutation({
    mutationFn: () => userAdminAPI.adjustPoints(userId, { amount: Number(amount), type: pointType, memo: memo || undefined }),
    onSuccess: () => { setPointDialog(false); setAmount(''); setMemo(''); invalidate(); },
  });

  const suspendMutation = useMutation({
    mutationFn: (suspend: boolean) => userAdminAPI.suspend(userId, { suspended: suspend }),
    onSuccess: invalidate,
  });

  const resetMutation = useMutation({
    mutationFn: (scope: 'daily' | 'points' | 'all') => userAdminAPI.reset(userId, { scope }),
    onSuccess: invalidate,
  });

  const resetDeviceMutation = useMutation({
    mutationFn: () => userAdminAPI.resetDevice(userId),
    onSuccess: () => { invalidate(); alert('기기 바인딩이 초기화되었습니다. 이제 해당 기기로 다시 가입/로그인할 수 있습니다.'); },
  });

  if (!user) return <Box>로딩 중...</Box>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/users${listSearch}`)}>목록</Button>
        <Typography variant="h4">회원 상세 #{user.id}</Typography>
        {user.isSuspended && <Chip label="정지됨" color="error" />}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>기본 정보</Typography>
            <Typography variant="body2">이메일: {user.email || '-'}</Typography>
            <Typography variant="body2">닉네임: {user.nickname || '-'}</Typography>
            <Typography variant="body2">로그인: {user.provider}</Typography>
            <Typography variant="body2">현재 직급: Lv.{user.level} {user.levelName}</Typography>
            <Typography variant="body2">경험치: {fmt(user.exp)} {user.nextLevelExp ? `/ 다음 ${fmt(user.nextLevelExp)}` : '(최고)'}</Typography>
            <Typography variant="body2">가입일: {formatKstDateTime(user.createdAt)}</Typography>
            <Typography variant="body2">deviceId: {user.deviceId || '-'} {user.deviceBlocked && <Chip label="기기차단" color="error" size="small" />}</Typography>
            <Typography variant="body2">IP: {user.ipAddress || '-'}</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => { if (window.confirm('이 회원의 기기 바인딩을 초기화할까요?\n(1기기1계정 제한이 풀려 해당 기기로 다시 가입/로그인 가능)')) resetDeviceMutation.mutate(); }}
            >
              디바이스 초기화
            </Button>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>포인트 / 미션</Typography>
            <Typography variant="body2">보유 포인트: <b>{fmt(user.points)} P</b></Typography>
            <Typography variant="body2">미션보너스 포인트: <b>{fmt(user.bonusPoints)} P</b></Typography>
            <Typography variant="body2">오늘 근무수당: {user.lastWorkRewardDate ? `수령(${user.lastWorkRewardDate})` : '미수령'}</Typography>
            <Typography variant="body2">오늘 광고: {user.adClaimedToday}/{user.adDailyLimit}회 (남은 {user.adRemainingToday})</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="contained" color="success" onClick={() => { setPointType('grant'); setPointDialog(true); }}>포인트 지급</Button>
              <Button size="small" variant="contained" color="error" onClick={() => { setPointType('confiscate'); setPointDialog(true); }}>포인트 몰수</Button>
              <Button size="small" variant="outlined" color={user.isSuspended ? 'success' : 'error'} onClick={() => suspendMutation.mutate(!user.isSuspended)}>
                {user.isSuspended ? '정지 해제' : '계정 정지'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => resetMutation.mutate('daily')}>일일 리셋</Button>
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 1 }}>포인트 적립/사용 내역</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>일시</TableCell>
              <TableCell>구분</TableCell>
              <TableCell>출처</TableCell>
              <TableCell align="right">금액</TableCell>
              <TableCell align="right">잔액</TableCell>
              <TableCell>메모</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {user.pointHistory?.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{formatKstDateTime(t.createdAt)}</TableCell>
                <TableCell>
                  <Chip label={t.type === 'earn' ? '적립' : '사용'} color={t.type === 'earn' ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>{SOURCE_LABEL[t.source] || t.source}{t.pointBucket === 'bonus' ? ' (보너스)' : ''}</TableCell>
                <TableCell align="right" style={{ color: t.type === 'earn' ? '#2e7d32' : '#c62828' }}>
                  {t.type === 'earn' ? '+' : '-'}{fmt(t.amount)}
                </TableCell>
                <TableCell align="right">{fmt(t.balanceAfter)}</TableCell>
                <TableCell>{t.memo || '-'}</TableCell>
              </TableRow>
            ))}
            {!user.pointHistory?.length && (
              <TableRow><TableCell colSpan={6} align="center">내역이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={pointDialog} onClose={() => setPointDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{pointType === 'grant' ? '포인트 지급' : '포인트 몰수'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>유형</InputLabel>
            <Select value={pointType} label="유형" onChange={(e) => setPointType(e.target.value as any)}>
              <MenuItem value="grant">지급(이벤트 당첨 등)</MenuItem>
              <MenuItem value="confiscate">몰수(어뷰징)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="포인트" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="메모(사유)" value={memo} onChange={(e) => setMemo(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPointDialog(false)}>취소</Button>
          <Button variant="contained" disabled={!amount || Number(amount) <= 0} onClick={() => pointMutation.mutate()}>적용</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;
